import { WeaponSlot, type WeaponDef } from '@vm/shared';
import { CAMO_BY_ID } from './cosmetics';

/**
 * Weapon mastery: per-weapon takedown tiers (bronze -> diamond).
 *
 * HARD DESIGN RULE: mastery is cosmetic status only. It unlocks the metallic mastery camo and shows a badge;
 * it must never change a weapon's stats (damage, spread, recoil, reload, ...) or anything the simulation reads.
 *
 * The flat base thresholds live on the mastery camos in `CAMOS` (50/150/400/1000). Weapons that are harder to
 * get takedowns with need fewer: each weapon class has a multiplier. Every multiplier is <= 1 ON PURPOSE, so
 * a threshold can only go down and no existing account can lose a tier or an equipped camo.
 */
export const MASTERY_TIERS = ['bronze', 'silver', 'gold', 'diamond'] as const;
export type MasteryTier = (typeof MASTERY_TIERS)[number];
/** The highest tier earned with a weapon, or 'none' (no badge is shown for 'none'). */
export type MasteryLevel = MasteryTier | 'none';

export type MasteryClass = 'primary' | 'shotgun' | 'backup' | 'melee' | 'heavy' | 'beam' | 'sniper' | 'special';

/** Fraction of the flat base thresholds each weapon class needs. All <= 1. */
export const MASTERY_CLASS_MULT: Readonly<Record<MasteryClass, number>> = {
  primary: 1,
  shotgun: 0.6,
  backup: 0.8,
  melee: 0.6,
  heavy: 0.5,
  beam: 0.4,
  sniper: 0.4,
  special: 1,
};

export function isMasteryTier(id: string | undefined | null): id is MasteryTier {
  return !!id && (MASTERY_TIERS as readonly string[]).includes(id);
}

/** Which mastery class a weapon belongs to (decides its threshold multiplier). */
export function masteryClass(w: WeaponDef): MasteryClass {
  // Mode-only tools and hidden weapons (builder, claws) keep the flat thresholds.
  if (w.hidden || w.slot === WeaponSlot.Tool || w.kind === 'builder') return 'special';
  if (w.kind === 'beam') return 'beam'; // flamethrower, mining laser, tesla coil
  if (w.slot === WeaponSlot.Sniper) return 'sniper'; // snipers + crossbow
  if (w.pellets && w.pellets > 1) return 'shotgun'; // includes the backup sawed-off
  if (w.slot === WeaponSlot.Melee || w.kind === 'melee') return 'melee';
  if (w.slot === WeaponSlot.Heavy) return 'heavy';
  if (w.slot === WeaponSlot.Backup) return 'backup';
  return 'primary'; // automatic / burst / battle-rifle primaries
}

/**
 * Takedowns needed with `weapon` for a mastery tier. `tier` is a tier id or the flat base kills number
 * (e.g. `camo.masteryKills`). Result is the base scaled by the weapon's class multiplier, rounded to a
 * multiple of 5 (minimum 5) and never above the base.
 */
export function masteryKillsFor(weapon: WeaponDef, tier: MasteryTier | number): number {
  let base: number;
  if (typeof tier === 'number') base = tier;
  else {
    const camoKills = CAMO_BY_ID[tier]?.masteryKills;
    if (!camoKills) throw new Error(`Unknown mastery tier ${tier}`);
    base = camoKills;
  }
  const mult = MASTERY_CLASS_MULT[masteryClass(weapon)];
  if (mult >= 1) return base;
  return Math.min(base, Math.max(5, Math.round((base * mult) / 5) * 5));
}

/** All four thresholds for a weapon. */
export function masteryThresholds(weapon: WeaponDef): Record<MasteryTier, number> {
  return {
    bronze: masteryKillsFor(weapon, 'bronze'),
    silver: masteryKillsFor(weapon, 'silver'),
    gold: masteryKillsFor(weapon, 'gold'),
    diamond: masteryKillsFor(weapon, 'diamond'),
  };
}

/** Highest tier reached with `kills` takedowns ('none' below bronze). */
export function masteryTierFor(weapon: WeaponDef, kills: number): MasteryLevel {
  let out: MasteryLevel = 'none';
  for (const t of MASTERY_TIERS) if (kills >= masteryKillsFor(weapon, t)) out = t;
  return out;
}

/** The next tier to work towards and its takedown target, or null when diamond is reached. */
export function nextMasteryTier(weapon: WeaponDef, kills: number): { tier: MasteryTier; kills: number } | null {
  for (const t of MASTERY_TIERS) {
    const need = masteryKillsFor(weapon, t);
    if (kills < need) return { tier: t, kills: need };
  }
  return null;
}
